<app-configuration-page
  title="Online Booking"
  description="Let visitors book their own intro lesson from the studio website. They pick a day and a free time; everything else is filled in for them."
  [breadcrumbs]="[{ label: 'Online Booking' }]">

  <div configPageActions>
    <button type="button" mat-flat-button color="primary" (click)="save()" [disabled]="showProgressSpinner">Save</button>
  </div>

  <mat-progress-spinner class="mx-auto my-3" *ngIf="showProgressSpinner" mode="indeterminate"></mat-progress-spinner>

  <form [formGroup]="form" *ngIf="!showProgressSpinner">
    <app-success-message [message]="successMessage"></app-success-message>
    <app-error-message [message]="errorMessage"></app-error-message>

    <section class="mt-3">
      <mat-card class="settings-card mb-4">
        <mat-card-header class="mb-3">
          <div mat-card-avatar class="card-icon"><mat-icon>event_available</mat-icon></div>
          <mat-card-title>What each web booking creates</mat-card-title>
          <mat-card-subtitle>Inquiry taker, executive and teacher are all set to <strong>Website Booking</strong>; on the Day View, drag the lesson from that column onto a teacher and its pre-chat follows. The confirmation email and text the visitor receives are edited under Communication Templates.</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <mat-slide-toggle color="primary" formControlName="field_booking_enabled" class="d-block">
            {{ form.get('field_booking_enabled')?.value ? 'Online booking is on' : 'Online booking is off' }}
          </mat-slide-toggle>
          <p class="text-muted small mt-3 mb-0">Each web booking enrolls the visitor on one of this studio's intro system packages (Dance Evaluation, Pre-Original or Pre-Original Couple), named by the snippet below, with a pre-chat 15 minutes before the lesson.</p>
        </mat-card-content>
      </mat-card>

      <mat-card class="settings-card mb-4">
        <mat-card-header class="mb-3">
          <div mat-card-avatar class="card-icon"><mat-icon>schedule</mat-icon></div>
          <mat-card-title>Scheduling window</mat-card-title>
          <mat-card-subtitle>Within Studio Working Hours, and Session Start Times when set; one web booking per start time</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div class="row">
            <div class="col-12 col-lg-4">
              <mat-form-field class="w-100" appearance="outline">
                <mat-label>Days ahead</mat-label>
                <input matInput type="number" min="1" max="90" formControlName="field_booking_days_ahead">
                <mat-hint>How far ahead visitors can book</mat-hint>
              </mat-form-field>
            </div>
            <div class="col-12 col-lg-4">
              <mat-form-field class="w-100" appearance="outline">
                <mat-label>Lead time (hours)</mat-label>
                <input matInput type="number" min="0" max="168" formControlName="field_booking_lead_hours">
                <mat-hint>Earliest booking from now</mat-hint>
              </mat-form-field>
            </div>
            <div class="col-12 col-lg-4">
              <mat-form-field class="w-100" appearance="outline">
                <mat-label>Start time spacing (minutes)</mat-label>
                <input matInput type="number" min="5" max="120" step="5" formControlName="field_booking_slot_minutes">
                <mat-hint>Ignored when Session Start Times are set</mat-hint>
              </mat-form-field>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="settings-card mb-4">
        <mat-card-header class="mb-3">
          <div mat-card-avatar class="card-icon"><mat-icon>code</mat-icon></div>
          <mat-card-title>Add to the studio website</mat-card-title>
          <mat-card-subtitle>Two lines, pasted where the form should appear</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <mat-button-toggle-group class="mb-3" [(ngModel)]="embedView" [ngModelOptions]="{standalone: true}" aria-label="What the embed shows">
            <mat-button-toggle value="form">Form only</mat-button-toggle>
            <mat-button-toggle value="full">Form with headline</mat-button-toggle>
          </mat-button-toggle-group>
          <p class="text-muted small" *ngIf="embedView === 'form'">Just the booking card. The page keeps its own headline, copy and bullets; the frame sizes itself to the form.</p>
          <p class="text-muted small" *ngIf="embedView === 'full'">The booking card with the "Book Your Free Intro Lesson Today" headline column beside it, for a page that has nothing else.</p>
          <mat-form-field appearance="outline" class="w-100" style="max-width: 420px;">
            <mat-label>System package this snippet books</mat-label>
            <mat-select [(ngModel)]="embedPackage" [ngModelOptions]="{standalone: true}">
              <mat-option *ngFor="let p of embedPackages" [value]="p.slug">{{ p.name }}</mat-option>
            </mat-select>
            <mat-hint>Paste one snippet per package to offer more than one form on the website</mat-hint>
          </mat-form-field>
          <p class="small text-warning-emphasis" *ngIf="websiteChecked && !embedPackages.length">
            This studio has no intro system packages (Dance Evaluation, Pre-Original, Pre-Original Couple), so web bookings cannot be enrolled. Run the system package backfill first.
          </p>
          <div class="d-flex align-items-start gap-2">
            <pre class="form-control font-monospace small mb-0 flex-grow-1" style="white-space: pre-wrap;">{{ embedSnippet }}</pre>
            <div class="d-flex flex-column gap-2">
              <button type="button" mat-stroked-button (click)="copy(embedSnippet, 'embed')">
                <mat-icon>{{ copied === 'embed' ? 'check' : 'content_copy' }}</mat-icon> {{ copied === 'embed' ? 'Copied' : 'Copy' }}
              </button>
              <!-- Opens exactly what this snippet embeds: same view and package. -->
              <a mat-stroked-button [href]="widgetUrl" target="_blank" rel="noopener" [class.disabled]="!embedKey">
                <mat-icon>visibility</mat-icon> Preview
              </a>
            </div>
          </div>
          <p class="text-muted small mt-2 mb-0" *ngIf="!website">
            Or link to the hosted page: <a [href]="widgetUrl" target="_blank" rel="noopener">{{ widgetUrl }}</a>
          </p>
          <p class="small mt-2 mb-0" *ngIf="website">
            <mat-icon class="align-middle" style="font-size: 18px; width: 18px; height: 18px;">lock</mat-icon>
            The form only loads on pages under <a [href]="website" target="_blank" rel="noopener">{{ website }}</a>, the studio's website slug. Staff who are logged in can still open it with Preview below.
          </p>
          <p class="small text-warning-emphasis mt-2 mb-0" *ngIf="websiteChecked && !website">
            <mat-icon class="align-middle" style="font-size: 18px; width: 18px; height: 18px;">warning_amber</mat-icon>
            This studio has no DXP website slug on record. Contact support, or sign up for DXP, to get one set up. Staff who are logged in can still Preview it.
          </p>

        </mat-card-content>
      </mat-card>
    </section>
  </form>
</app-configuration-page>
